/**
 * @zendir/ui — Semantic themes (Layer 1).
 *
 * Each theme maps semantic roles (background, text, border, accent, status,
 * semantic, interactive) onto concrete values. The nebula theme is fully
 * palette-aligned — it references the Layer 0 primitives in
 * src/tokens/palettes. The astro / hybrid / purple-hue / transparent themes
 * predate the palette system and keep their original literal values until
 * design migrates them onto endorsed scales.
 *
 * Status colors are immutable across every theme — always `astroStatus`
 * (operator-grade severity must never shift with the visual theme).
 *
 * These definitions are consumed by two surfaces:
 *   • ThemeProvider (runtime) — resolves the active variant into CSS vars.
 *   • src/tokens/index.ts (build) — emits the static tokens.css stylesheet.
 * The shared token shapes live in ../react/theme/types so neither surface
 * has to pull in the other.
 */

import type {
  ThemeTokensBase,
  ThemeFocus,
  ThemeAnimation,
  LayoutTokens,
} from "../react/theme/types";
import {
  ultraviolet,
  electricBlue,
  zendirNeutral,
  astroStatus,
} from "./palettes";
import {
  green as twGreen,
  yellow as twYellow,
  red as twRed,
} from "./palettes/tailwind";

// ============================================================================
// Animation Tokens (Shared)
// ============================================================================

export const animationTokens: ThemeAnimation = {
  fast: "all 150ms cubic-bezier(0.4, 0, 0.2, 1)",
  normal: "all 250ms cubic-bezier(0.4, 0, 0.2, 1)",
  slow: "all 400ms cubic-bezier(0.4, 0, 0.2, 1)",
  spring: "all 300ms cubic-bezier(0.34, 1.56, 0.64, 1)",
  easing: {
    default: "cubic-bezier(0.4, 0, 0.2, 1)",
    in: "ease-in",
    out: "ease-out",
    inOut: "ease-in-out",
  },
  duration: {
    instant: 0,
    fast: 150,
    normal: 250,
    slow: 400,
  },
};

export const animationTokensReduced: ThemeAnimation = {
  fast: "all 0.01ms linear",
  normal: "all 0.01ms linear",
  slow: "all 0.01ms linear",
  spring: "all 0.01ms linear",
  easing: animationTokens.easing,
  duration: { instant: 0, fast: 0, normal: 0, slow: 0 },
};

// ============================================================================
// Focus Tokens
// ============================================================================

export const focusTokensDark: ThemeFocus = {
  color: "#4dacff",
  width: "2px",
  offset: "2px",
  ring: "0 0 0 2px #4dacff",
  style: {
    outline: "2px solid #4dacff",
    outlineOffset: "2px",
  },
};

export const focusTokensLight: ThemeFocus = {
  color: "#0066cc",
  width: "2px",
  offset: "2px",
  ring: "0 0 0 2px #0066cc",
  style: {
    outline: "2px solid #0066cc",
    outlineOffset: "2px",
  },
};

export const focusTokensPurpleHue: ThemeFocus = {
  color: "#8b5cf6",
  width: "2px",
  offset: "2px",
  ring: "0 0 0 2px #8b5cf6",
  style: {
    outline: "2px solid #8b5cf6",
    outlineOffset: "2px",
  },
};

// ============================================================================
// Shadow Tokens
// ============================================================================

export const shadowsDark = {
  none: "none",
  sm: "0 1px 2px rgba(0, 0, 0, 0.3)",
  md: "0 4px 8px rgba(0, 0, 0, 0.4)",
  lg: "0 8px 16px rgba(0, 0, 0, 0.5)",
  xl: "0 16px 32px rgba(0, 0, 0, 0.6)",
  glow: (color: string) => `0 0 20px ${color}40, 0 0 40px ${color}20`,
};

export const shadowsLight = {
  none: "none",
  sm: "0 1px 2px rgba(0, 0, 0, 0.1)",
  md: "0 4px 8px rgba(0, 0, 0, 0.15)",
  lg: "0 8px 16px rgba(0, 0, 0, 0.2)",
  xl: "0 16px 32px rgba(0, 0, 0, 0.25)",
  glow: (color: string) => `0 0 20px ${color}30, 0 0 40px ${color}15`,
};

// ============================================================================
// Layout Tokens (Shared)
// Semantic layout spacing based on 4px grid. Shared across all themes.
// ============================================================================

export const layoutTokens: LayoutTokens = {
  card: {
    padding: "16px", // 4 × 4px
    gap: "24px", // 6 × 4px  — desktop grid gap (matches outer padding)
    gapCompact: "16px", // 4 × 4px  — mobile grid gap
    heading: {
      iconSize: 18, // default card header icon (px) — matches ISS/Telemetry card header size
      iconSizeCompact: 16, // compact (e.g. chart inline header)
      titleFontSize: "1rem", // card titles (ISS, Telemetry, Mission) — reduced from 1.125rem
      titleFontSizeChart: "0.875rem", // chart titles (Power Trends) — slightly smaller
      titleFontWeight: 500,
      gap: 8, // gap between icon and title (px)
    },
  },
  section: {
    headerPaddingBottom: "8px", // 2 × 4px
    headerMarginBottom: "0px",
    contentPaddingTop: "8px", // 2 × 4px
  },
  form: {
    fieldGap: "12px", // 3 × 4px  — between stacked fields
    inlineGap: "12px", // 3 × 4px  — between side-by-side fields
    groupPadding: "12px", // 3 × 4px  — inside grouped containers
    groupGap: "12px", // 3 × 4px  — within group items
    labelGap: "3px", // label text ↔ tooltip icon
  },
  surface: {
    padding: "12px", // 3 × 4px
    borderRadius: "12px", // 3 × 4px
    gap: "8px", // 2 × 4px
  },
};

// ============================================================================
// Astro UX Dark Theme
// ============================================================================

export const astroThemeDark: ThemeTokensBase = {
  colors: {
    background: {
      base: "#101923",
      surface: "#1b2d3e",
      elevated: "#243b53",
      overlay: "rgba(16, 25, 35, 0.95)",
    },
    border: {
      default: "#2b659b",
      muted: "#172635",
      focus: "#4dacff",
    },
    text: {
      primary: "#ffffff",
      secondary: "#b7c5d3",
      tertiary: "#8fa4b7",
      muted: "#8fa4b7",
      inverse: "#1b2d3e",
    },
    status: { ...astroStatus },
    semantic: {
      success: "#56f000",
      warning: "#fce83a",
      error: "#ff3838",
      info: "#4dacff",
    },
    accent: {
      primary: "#4dacff",
      secondary: "#56f000",
      tertiary: "#a371f7",
    },
    interactive: {
      default: "#4dacff",
      hover: "#92cbff",
      active: "#0066cc",
      disabled: "#6b7280",
    },
  },
  spacing: {
    xxs: "2px",
    xs: "4px",
    sm: "8px",
    smd: "12px",
    md: "16px",
    mdl: "20px",
    lg: "24px",
    xl: "32px",
    xxl: "48px",
  },
  borderRadius: {
    none: "0",
    xs: "1px",
    sm: "2px",
    md: "4px",
    lg: "8px",
    xl: "12px",
    full: "9999px",
  },
  elementSize: {
    sm: "28px",
    md: "36px",
    lg: "44px",
  },
  typography: {
    /**
     * Font families per AstroUXDS specification
     * @see https://www.astrouxds.com/foundations/typography/
     */
    fontFamily: {
      /** Primary font: Roboto with comprehensive system fallbacks */
      primary:
        '"Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
      /** Heading font: Roboto per AstroUXDS (matches primary). */
      heading:
        '"Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
      /** Monospace font: Roboto Mono for data/code with system fallbacks */
      mono: '"Roboto Mono", "SF Mono", "Consolas", "Liberation Mono", monospace',
    },
    // Astro UX Display Styles
    // https://www.astrouxds.com/foundations/typography/
    display: {
      1: {
        fontSize: "3.75rem",
        fontWeight: 300,
        letterSpacing: "-0.5px",
        lineHeight: "4.375rem",
      },
      2: {
        fontSize: "3rem",
        fontWeight: 400,
        letterSpacing: "0",
        lineHeight: "3.5rem",
      },
    },
    // Astro UX Heading Styles
    heading: {
      1: {
        fontSize: "2.125rem",
        fontWeight: 400,
        letterSpacing: "0.25px",
        lineHeight: "2.5rem",
      },
      "1Bold": {
        fontSize: "2.125rem",
        fontWeight: 700,
        letterSpacing: "0.25px",
        lineHeight: "2rem",
      },
      2: {
        fontSize: "1.5rem",
        fontWeight: 400,
        letterSpacing: "0",
        lineHeight: "1.75rem",
      },
      3: {
        fontSize: "1.25rem",
        fontWeight: 500,
        letterSpacing: "0.15px",
        lineHeight: "1.5rem",
      },
      4: {
        fontSize: "1.25rem",
        fontWeight: 300,
        letterSpacing: "0.15px",
        lineHeight: "1.5rem",
      },
      5: {
        fontSize: "1.125rem",
        fontWeight: 400,
        letterSpacing: "0",
        lineHeight: "1.5rem",
      },
      6: {
        fontSize: "1.125rem",
        fontWeight: 300,
        letterSpacing: "0",
        lineHeight: "1.5rem",
      },
    },
    // Astro UX Body Styles
    body: {
      1: {
        fontSize: "1rem",
        fontWeight: 400,
        letterSpacing: "0.5px",
        lineHeight: "1.5rem",
      },
      "1Bold": {
        fontSize: "1rem",
        fontWeight: 700,
        letterSpacing: "0.5px",
        lineHeight: "1.5rem",
      },
      2: {
        fontSize: "0.875rem",
        fontWeight: 400,
        letterSpacing: "0.5px",
        lineHeight: "1.25rem",
      },
      "2Bold": {
        fontSize: "0.875rem",
        fontWeight: 700,
        letterSpacing: "0.5px",
        lineHeight: "1.25rem",
      },
      3: {
        fontSize: "0.75rem",
        fontWeight: 400,
        letterSpacing: "0",
        lineHeight: "1rem",
      },
      "3Bold": {
        fontSize: "0.75rem",
        fontWeight: 700,
        letterSpacing: "0",
        lineHeight: "1rem",
      },
    },
    // Astro UX Control Body Styles
    control: {
      1: {
        fontSize: "1rem",
        fontWeight: 400,
        letterSpacing: "0.5px",
        lineHeight: "1.25rem",
      },
      "1Bold": {
        fontSize: "1rem",
        fontWeight: 700,
        letterSpacing: "0.5px",
        lineHeight: "1.25rem",
      },
    },
    // Legacy convenience tokens (rem-based for Astro compliance)
    // Note: Use Typography component for new code
    fontSize: {
      micro: "0.5625rem", // 9px - very tight spaces
      xxs: "0.625rem", // 10px - compact labels (xs in old system)
      xs: "0.625rem", // 10px - compact (kept for backward compatibility)
      sm: "0.75rem", // 12px (Body 3)
      base: "0.875rem", // 14px (Body 2)
      md: "0.875rem", // 14px (Body 2)
      lg: "1rem", // 16px (Body 1)
      xl: "1.25rem", // 20px (Heading 3/4)
      xxl: "1.5rem", // 24px (Heading 2)
      xxxl: "2.125rem", // 34px (Heading 1)
    },
    /**
     * Font weights per AstroUXDS specification
     * Only 300, 400, 500, 700 are officially supported
     * @see https://www.astrouxds.com/foundations/typography/
     */
    fontWeight: {
      light: 300,
      normal: 400,
      medium: 500,
      /** @deprecated Use medium (500) instead - Astro spec only supports 300, 400, 500, 700 */
      semibold: 500, // Changed from 600 to 500 for Astro compliance
      bold: 700,
    },
    lineHeight: {
      tight: "1.25rem",
      normal: "1.5rem",
      relaxed: "1.75rem",
    },
    letterSpacing: {
      tight: "-0.5px",
      normal: "0",
      wide: "0.5px",
    },
  },
  shadows: shadowsDark,
  animation: animationTokens,
  focus: focusTokensDark,
  layout: layoutTokens,
};

// ============================================================================
// Astro UX Light Theme
// ============================================================================

export const astroThemeLight: ThemeTokensBase = {
  colors: {
    background: {
      base: "#eaeef4",
      surface: "#f5f8fc",
      elevated: "#ffffff",
      overlay: "rgba(234, 238, 244, 0.95)",
    },
    border: {
      default: "#2b659b",
      muted: "#d1d5db",
      focus: "#0066cc",
    },
    text: {
      primary: "#1b2d3e",
      secondary: "#51667c",
      tertiary: "#556a7c", // WCAG AA: 4.81:1 on #eaeef4, 5.24:1 on #f5f8fc, 5.61:1 on #fff (was #8fa4b7 → 2.25:1 FAIL)
      muted: "#556a7c", // WCAG AA compliant (was #8fa4b7)
      inverse: "#ffffff",
    },
    status: { ...astroStatus },
    semantic: {
      success: "#56f000",
      warning: "#fce83a",
      error: "#ff3838",
      info: "#4dacff",
    },
    accent: {
      primary: "#0066cc",
      secondary: "#56f000",
      tertiary: "#7c3aed",
    },
    interactive: {
      default: "#0066cc",
      hover: "#0052a3",
      active: "#003d7a",
      disabled: "#a4abb6",
    },
  },
  spacing: astroThemeDark.spacing,
  borderRadius: astroThemeDark.borderRadius,
  elementSize: astroThemeDark.elementSize,
  typography: astroThemeDark.typography,
  shadows: shadowsLight,
  animation: animationTokens,
  focus: focusTokensLight,
  layout: layoutTokens,
};

// ============================================================================
// Hybrid Theme (Zendir Background + Astro Status + Purple Accents)
// Modern, award-winning design with Zendir purple personality
// ============================================================================

export const hybridTheme: ThemeTokensBase = {
  colors: {
    background: {
      base: "#09090b",
      surface: "rgba(24, 24, 27, 0.98)",
      elevated: "rgba(39, 39, 42, 0.98)",
      overlay: "rgba(0, 0, 0, 0.85)",
    },
    border: {
      default: "rgba(139, 92, 246, 0.3)", // Purple tint
      muted: "rgba(63, 63, 70, 0.5)",
      focus: "#8b5cf6", // Zendir purple
    },
    text: {
      primary: "#ffffff",
      secondary: "#a1a1aa",
      tertiary: "#8e8e96", // WCAG AA: 6.12:1 on #09090b, 5.45:1 on #18181b, 4.58:1 on #27272a (was #71717a → 4.07:1 FAIL)
      muted: "#8e8e96", // WCAG AA compliant (was #71717a)
      inverse: "#09090b",
    },
    // Astro UXD Status Colors (unchanged for compliance)
    status: { ...astroStatus },
    semantic: {
      success: "#56f000",
      warning: "#fce83a",
      error: "#ff3838",
      info: "#8b5cf6", // Purple for info
    },
    // Zendir Purple Accent Palette
    accent: {
      primary: "#8b5cf6", // Vibrant purple
      secondary: "#a78bfa", // Light purple
      tertiary: "#7c3aed", // Deep purple
    },
    interactive: {
      default: "#8b5cf6",
      hover: "#a78bfa",
      active: "#7c3aed",
      disabled: "#52525b",
    },
  },
  spacing: astroThemeDark.spacing,
  borderRadius: {
    none: "0",
    xs: "2px",
    sm: "4px",
    md: "6px",
    lg: "10px",
    xl: "14px",
    full: "9999px",
  },
  elementSize: astroThemeDark.elementSize,
  typography: astroThemeDark.typography,
  shadows: {
    ...shadowsDark,
    glow: (color: string) =>
      `0 0 20px ${color}50, 0 0 40px ${color}25, 0 4px 12px ${color}20`,
  },
  animation: animationTokens,
  focus: focusTokensPurpleHue,
  layout: layoutTokens,
};

// ============================================================================
// Purple-Hue Theme - Full Purple Personality
// Award-winning dark theme with purple accents
// ============================================================================

export const purpleHueTheme: ThemeTokensBase = {
  colors: {
    background: {
      base: "#0a0a0f",
      surface: "rgba(18, 18, 28, 0.98)",
      elevated: "rgba(28, 28, 42, 0.98)",
      overlay: "rgba(0, 0, 0, 0.9)",
    },
    border: {
      default: "rgba(139, 92, 246, 0.25)",
      muted: "rgba(55, 48, 82, 0.5)",
      focus: "#8b5cf6",
    },
    text: {
      primary: "#fafafa",
      secondary: "#a1a1aa",
      tertiary: "#8e8e96", // WCAG AA: 6.08:1 on #0a0a0f, 5.02:1 on elevated (was #71717a → 4.05:1 FAIL)
      muted: "#8e8e96", // WCAG AA compliant (was #71717a)
      inverse: "#0a0a0f",
    },
    // Astro UXD Status Colors (for compliance)
    status: { ...astroStatus },
    semantic: {
      success: "#56f000",
      warning: "#fce83a",
      error: "#ff3838",
      info: "#a78bfa",
    },
    // Zendir Purple Accent Palette
    accent: {
      primary: "#8b5cf6", // Vibrant purple
      secondary: "#a78bfa", // Light purple
      tertiary: "#7c3aed", // Deep purple
    },
    interactive: {
      default: "#8b5cf6",
      hover: "#a78bfa",
      active: "#7c3aed",
      disabled: "#52525b",
    },
  },
  spacing: astroThemeDark.spacing,
  borderRadius: {
    none: "0",
    xs: "2px",
    sm: "4px",
    md: "6px",
    lg: "10px",
    xl: "14px",
    full: "9999px",
  },
  elementSize: astroThemeDark.elementSize,
  // Same typography as hybrid (Astro UX font stack with full fallbacks) for consistent card font across themes
  typography: astroThemeDark.typography,
  shadows: {
    ...shadowsDark,
    glow: (color: string) =>
      `0 0 25px ${color}60, 0 0 50px ${color}30, 0 4px 16px ${color}25`,
  },
  animation: animationTokens,
  focus: {
    ...focusTokensPurpleHue,
  },
  layout: layoutTokens,
};

// ============================================================================
// Nebula Theme — palette-native dark theme.
//
// The first theme wired end-to-end onto the Layer 0 primitives: Prussian
// Slate (zendirNeutral) surfaces + text, Ultraviolet accent/interactive,
// Astro UXDS status, and Tailwind + Electric Blue semantic colors.
// ============================================================================

// The nebula surface spec defines two stops that sit between Neutral ramp
// values (deeper than neutral-950 reads on OLED, between 900 and 800 for
// elevation). They are part of the Nebula surface spec, not the ramp.
const nebulaBase = "#070A18"; // --bg
const nebulaElevated = "#121B36"; // --surface-2 / --border-subtle

export const nebulaTheme: ThemeTokensBase = {
  colors: {
    background: {
      base: nebulaBase,
      surface: zendirNeutral[900], // --surface-1
      elevated: nebulaElevated,
      overlay: "rgba(7, 10, 24, 0.9)", // nebulaBase @ 90%
    },
    border: {
      default: zendirNeutral[800], // --border / --surface-3
      muted: nebulaElevated, // --border-subtle
      focus: ultraviolet[500],
    },
    text: {
      primary: zendirNeutral[50], // --text
      secondary: zendirNeutral[300], // --text-2
      tertiary: zendirNeutral[400], // --text-3
      muted: zendirNeutral[500], // --text-4
      inverse: nebulaBase,
    },
    // Astro UXDS status colors — operator-grade severity, kept immutable.
    status: { ...astroStatus },
    semantic: {
      success: twGreen[500],
      warning: twYellow[500],
      error: twRed[500],
      info: electricBlue[500],
    },
    // Ultraviolet accent ramp.
    accent: {
      primary: ultraviolet[500],
      secondary: ultraviolet[400],
      tertiary: ultraviolet[600],
    },
    interactive: {
      default: ultraviolet[500],
      hover: ultraviolet[400],
      active: ultraviolet[700],
      disabled: zendirNeutral[600], // --text-5
    },
  },
  spacing: astroThemeDark.spacing,
  borderRadius: {
    none: "0",
    xs: "2px",
    sm: "4px",
    md: "7px",
    lg: "10px",
    xl: "14px",
    full: "9999px",
  },
  elementSize: astroThemeDark.elementSize,
  typography: astroThemeDark.typography,
  shadows: {
    ...shadowsDark,
    glow: (color: string) =>
      `0 0 25px ${color}60, 0 0 50px ${color}30, 0 4px 16px ${color}25`,
  },
  animation: animationTokens,
  focus: {
    ...focusTokensPurpleHue,
  },
  layout: layoutTokens,
};

// ============================================================================
// Transparent Purple-Hue Theme - Glassmorphic Panels
// Optimized for glass card overlays with transparent backgrounds
// ============================================================================

export const transparentTheme: ThemeTokensBase = {
  colors: {
    background: {
      // Highly transparent backgrounds for glass effect
      base: "rgba(8, 12, 20, 0.95)",
      surface: "rgba(15, 20, 35, 0.85)",
      elevated: "rgba(25, 35, 55, 0.80)",
      overlay: "rgba(0, 0, 0, 0.75)",
    },
    border: {
      // Purple accent borders (Zendir brand)
      default: "rgba(139, 92, 246, 0.25)",
      muted: "rgba(139, 92, 246, 0.15)",
      focus: "#8b5cf6",
      // Thin faded card border with purple hint
      fadedBoxShadow:
        "inset 0 1px 0 rgba(139, 92, 246, 0.12), inset 0 -1px 0 rgba(0, 0, 0, 0.05), inset 1px 0 0 rgba(139, 92, 246, 0.06), inset -1px 0 0 rgba(139, 92, 246, 0.06)",
    },
    text: {
      primary: "#f0f4f8",
      secondary: "#94a3b8",
      tertiary: "#7b8da1", // WCAG AA: 5.71:1 on base, 5.34:1 on elevated (was #64748b → 3.66:1 FAIL)
      muted: "#7b8da1", // WCAG AA compliant (was #64748b)
      inverse: "#0f172a",
    },
    // Astro UXD Status Colors
    status: { ...astroStatus },
    semantic: {
      success: "#56f000",
      warning: "#fce83a",
      error: "#ff3838",
      info: "#8b5cf6",
    },
    // Purple accent (matching hybrid theme for consistency)
    accent: {
      primary: "#8b5cf6", // Vibrant purple
      secondary: "#a78bfa", // Light purple
      tertiary: "#7c3aed", // Deep purple
    },
    interactive: {
      default: "#8b5cf6", // Purple for interactive elements
      hover: "#a78bfa", // Light purple on hover
      active: "#7c3aed", // Deep purple on active
      disabled: "#475569",
      // Purple-hue transparent backgrounds for buttons/inputs (transparent + transparent-bold)
      transparentDefault: "rgba(139, 92, 246, 0.12)",
      transparentHover: "rgba(139, 92, 246, 0.32)",
      transparentInputBg: "rgba(139, 92, 246, 0.08)",
    },
  },
  spacing: astroThemeDark.spacing,
  borderRadius: {
    none: "0",
    xs: "2px",
    sm: "4px",
    md: "8px",
    lg: "12px",
    xl: "16px",
    full: "9999px",
  },
  elementSize: astroThemeDark.elementSize,
  // Same typography as hybrid for consistent card font across themes
  typography: astroThemeDark.typography,
  shadows: {
    ...shadowsDark,
    // Enhanced glow shadows for glass effect
    glow: (color: string) =>
      `0 0 30px ${color}50, 0 0 60px ${color}25, 0 4px 20px rgba(0,0,0,0.4)`,
  },
  animation: animationTokens,
  focus: {
    color: "#8b5cf6", // Purple focus ring
    width: "2px",
    offset: "2px",
    ring: "0 0 0 2px #8b5cf6",
    style: {
      outline: "2px solid #8b5cf6",
      outlineOffset: "2px",
    },
  },
  layout: layoutTokens,
};

// ============================================================================
// Transparent Minimal Theme - Ultra-clean glassmorphic panels
// Extremely subtle borders, minimal visual noise, clean dark surfaces
// ============================================================================

export const transparentMinimalTheme: ThemeTokensBase = {
  colors: {
    background: {
      // Same as transparent theme
      base: "rgba(8, 12, 20, 0.95)",
      surface: "rgba(15, 20, 35, 0.85)",
      elevated: "rgba(25, 35, 55, 0.80)",
      overlay: "rgba(0, 0, 0, 0.75)",
    },
    border: {
      // Accent-colored borders for minimal theme (Zendir purple)
      default: "rgba(139, 92, 246, 0.25)",
      muted: "rgba(139, 92, 246, 0.15)",
      focus: "#8b5cf6",
      // Subtle card border using accent color
      fadedBoxShadow:
        "inset 0 1px 0 rgba(139, 92, 246, 0.1), inset 0 -1px 0 rgba(0, 0, 0, 0.1), inset 1px 0 0 rgba(139, 92, 246, 0.05), inset -1px 0 0 rgba(139, 92, 246, 0.05)",
    },
    text: {
      primary: "#f0f4f8",
      secondary: "#94a3b8",
      tertiary: "#7b8da1", // WCAG AA: 5.71:1 on base, 5.34:1 on elevated (was #64748b → 3.66:1 FAIL)
      muted: "#7b8da1", // WCAG AA compliant (was #64748b)
      inverse: "#0f172a",
    },
    // Astro UXD Status Colors
    status: { ...astroStatus },
    semantic: {
      success: "#56f000",
      warning: "#fce83a",
      error: "#ff3838",
      info: "#8b5cf6",
    },
    // Purple accent (matching transparent theme for consistency)
    accent: {
      primary: "#8b5cf6", // Vibrant purple
      secondary: "#a78bfa", // Light purple
      tertiary: "#7c3aed", // Deep purple
    },
    interactive: {
      default: "#8b5cf6", // Purple for interactive elements
      hover: "#a78bfa", // Light purple on hover
      active: "#7c3aed", // Deep purple on active
      disabled: "#475569",
      // Purple-hue transparent backgrounds for buttons/inputs (same as transparent theme)
      transparentDefault: "rgba(139, 92, 246, 0.12)",
      transparentHover: "rgba(139, 92, 246, 0.32)",
      transparentInputBg: "rgba(139, 92, 246, 0.08)",
    },
  },
  spacing: astroThemeDark.spacing,
  borderRadius: {
    none: "0",
    xs: "2px",
    sm: "4px",
    md: "8px",
    lg: "12px",
    xl: "16px",
    full: "9999px",
  },
  elementSize: astroThemeDark.elementSize,
  // Same typography as hybrid for consistent card font across themes
  typography: astroThemeDark.typography,
  shadows: {
    ...shadowsDark,
    // Same glow as transparent theme
    glow: (color: string) =>
      `0 0 30px ${color}50, 0 0 60px ${color}25, 0 4px 20px rgba(0,0,0,0.4)`,
  },
  animation: animationTokens,
  focus: {
    color: "#8b5cf6", // Purple focus ring
    width: "2px",
    offset: "2px",
    ring: "0 0 0 2px #8b5cf6",
    style: {
      outline: "2px solid #8b5cf6",
      outlineOffset: "2px",
    },
  },
  layout: layoutTokens,
};
